import React, { createContext, useContext, useCallback, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { getBonuses, getEquipments, getMaterials, getProjectsWithTask, getReports, getTimeEntriesLastMonth, getDailyReports, getAddresses, getEmployeeByMyoctansId, getTasks } from '@/api';
import { useGlobals } from '@/context';

interface PrefetchContextType {
    prefetchAll: () => void;
}

const PrefetchContext = createContext<PrefetchContextType | undefined>(undefined);

export const PrefetchProvider = ({ children }: { children: React.ReactNode }): React.ReactElement => {
    const queryClient = useQueryClient();
    const [state, actions] = useGlobals();

    const prefetchProjectsWithTasks = useCallback(() => {
        queryClient.prefetchQuery({
            queryKey: ['projects-tasks'],
            queryFn: getProjectsWithTask,
        });
    }, [queryClient, state.apiToken]);

    const prefetchEquipments = useCallback(() => {
        queryClient.prefetchQuery({
            queryKey: ['equipments'],
            queryFn: getEquipments,
        });
    }, [queryClient, state.apiToken]);

    const prefetchMaterials = useCallback(() => {
        queryClient.prefetchQuery({
            queryKey: ['materials'],
            queryFn: getMaterials,
        });
    }, [queryClient, state.apiToken]);

    const prefetchBonuses = useCallback(() => {
        queryClient.prefetchQuery({
            queryKey: ['bonuses'],
            queryFn: getBonuses,
        });
    }, [queryClient, state.apiToken]);

    const prefetchTimeEntries = useCallback(() => {
        queryClient.prefetchQuery({
            queryKey: ['time-entries'],
            queryFn: getTimeEntriesLastMonth,
        })
    }, [queryClient, state.apiToken]);

    const prefetchReports = useCallback(() => {
        queryClient.prefetchQuery({
            queryKey: ['forms'],
            queryFn: getReports,
        })
    }, [queryClient, state.apiToken]);

    const prefetchDailyReports = useCallback(() => {
        queryClient.prefetchQuery({
            queryKey: ['daily-reports'],
            queryFn: getDailyReports,
        })
    }, [queryClient, state.apiToken]);

    const prefetchAddresses = useCallback(() => {
        queryClient.prefetchQuery({
            queryKey: ['addresses'],
            queryFn: getAddresses,
        })
    }, [queryClient, state.apiToken]);

    const prefetchEmployee = useCallback(() => {
        queryClient.prefetchQuery({
            queryKey: ['employee', state.user?.id],
            queryFn: ({ queryKey }) => getEmployeeByMyoctansId(queryKey[1] as number),
        })
    }, [queryClient, state.apiToken]);

    const prefetchTasks = useCallback(() => {
        queryClient.prefetchQuery({
            queryKey: ['tasks'],
            queryFn: getTasks,
        })
    }, [queryClient, state.apiToken]);

    const prefetchAll = useCallback(() => {
        prefetchProjectsWithTasks();
        prefetchEquipments();
        prefetchMaterials();
        prefetchBonuses();
        prefetchTimeEntries();
        prefetchReports();
        prefetchDailyReports();
        prefetchAddresses();
        prefetchEmployee();
        prefetchTasks();
    }, [state.apiToken]);


    return (
        <PrefetchContext.Provider
            value={{
                prefetchAll
            }}
        >
            {children}
        </PrefetchContext.Provider>
    );
};

export const usePrefetch = (): PrefetchContextType => {
    const context = useContext(PrefetchContext);
    if (!context) {
        throw new Error('usePrefetch must be used within a PrefetchProvider');
    }
    return context;
};